import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "const/axios";
import { API_URL } from "const/constants";

/**
 * The monthly received-commission ledger of one account row. Each month lands on its own the
 * moment it is entered rather than riding along with the whole-account save, so a half-finished
 * account form can never lose a month that was already recorded.
 */
export const commissionLedgerKey = (accountDetailId) => ["commissionMonths", accountDetailId];

const ledgerUrl = (accountDetailId) =>
  `${API_URL}/account-details/${accountDetailId}/commission-months`;
const monthUrl = (commissionMonthId) => `${API_URL}/commission-months/${commissionMonthId}`;

const emptyLedger = (accountDetailId) => ({
  accountDetailId,
  months: [],
  totalReceived: "0.00",
  commissionCompleted: false,
});

export const useCommissionLedger = (accountDetailId, { enabled = true } = {}) =>
  useQuery({
    queryKey: commissionLedgerKey(accountDetailId),
    queryFn: () =>
      axios
        .get(ledgerUrl(accountDetailId))
        .then((response) => response.data?.data ?? emptyLedger(accountDetailId)),
    enabled: Boolean(enabled) && accountDetailId != null,
    // The dialog is opened straight after an edit elsewhere often enough that a cached ledger
    // would show a month the server no longer has.
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });

/** Every mutation refreshes both the ledger and the account itself: the row's commission total,
 * profit and completion state are all recomputed server-side from the months. */
const useLedgerMutation = (accountDetailId, mutationFn, options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: commissionLedgerKey(accountDetailId) });
      options.onAccountChanged?.();
      options.onSuccess?.(...args);
    },
    onError: options.onError,
  });
};

export const useCreateCommissionMonth = (accountDetailId, options) =>
  useLedgerMutation(
    accountDetailId,
    (payload) => axios.post(ledgerUrl(accountDetailId), payload).then((r) => r.data?.data),
    options
  );

export const useUpdateCommissionMonth = (accountDetailId, options) =>
  useLedgerMutation(
    accountDetailId,
    ({ commissionMonthId, ...payload }) =>
      axios.put(monthUrl(commissionMonthId), payload).then((r) => r.data?.data),
    options
  );

export const useDeleteCommissionMonth = (accountDetailId, options) =>
  useLedgerMutation(
    accountDetailId,
    (commissionMonthId) => axios.delete(monthUrl(commissionMonthId)).then((r) => r.data),
    options
  );

/** Plain call for the card's toggle, which lives outside any one row's dialog. */
export const setCommissionCompletedRequest = (accountDetailId, completed) =>
  axios
    .patch(`${API_URL}/account-details/${accountDetailId}/commission-completed`, null, {
      params: { completed },
    })
    .then((response) => response.data?.data);

export const useSetCommissionCompleted = (accountDetailId, options) =>
  useLedgerMutation(
    accountDetailId,
    (completed) =>
      axios
        .patch(`${API_URL}/account-details/${accountDetailId}/commission-completed`, null, {
          params: { completed },
        })
        .then((r) => r.data?.data),
    options
  );

/** The notes written against one month. Fetched separately from the ledger so opening a month's
 * note thread never re-reads every other month with it. */
export const commissionMonthNotesKey = (commissionMonthId) => [
  "commissionMonthNotes",
  commissionMonthId,
];

export const useCommissionMonthNotes = (commissionMonthId, { enabled = true } = {}) =>
  useQuery({
    queryKey: commissionMonthNotesKey(commissionMonthId),
    queryFn: () =>
      axios
        .get(`${API_URL}/notes`, {
          params: { parentId: commissionMonthId, parentType: "COMMISSION_MONTH" },
        })
        .then((response) => response.data?.data ?? []),
    enabled: Boolean(enabled) && commissionMonthId != null,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });
